import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import axios from 'axios';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { LAST_TRANSITION } from '../../util/types';
import { sendEmailFordailyLogs, updateMetaData, uploadImageToGoogleStorageAPI } from '../../util/api';
import { generateUUID } from '../../util/data';
import { fetchCurrentUser } from '../../ducks/user.duck';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const CREATE_DAILY_LOG_REQUEST = 'app/InboxPage/CREATE_DAILY_LOG_REQUEST';
export const CREATE_DAILY_LOG_SUCCESS = 'app/InboxPage/CREATE_DAILY_LOG_SUCCESS';
export const CREATE_DAILY_LOG_ERROR = 'app/InboxPage/CREATE_DAILY_LOG_ERROR';


export const UPLOAD_IMAGE_REQUEST = 'app/InboxPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/InboxPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/InboxPage/UPLOAD_IMAGE_ERROR';
// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  uploadImageInProgress: false,
  uploadImageErorr: null,
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };
    case UPLOAD_IMAGE_REQUEST:
      return {
        ...state,
        uploadImageInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS:
      return {
        ...state,
        uploadImageInProgress: false,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_ERROR:
      return {
        ...state,
        uploadImageInProgress: false,
        uploadImageError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});


export const createDailyLogRequest = () => ({
  type: CREATE_DAILY_LOG_REQUEST,
});
export const createDailyLogSuccess = () => ({
  type: CREATE_DAILY_LOG_SUCCESS,
});
export const createDailyLogError = e => ({
  type: CREATE_DAILY_LOG_ERROR,
  error: true,
  payload: e,
});

export const uploadImageRequest = () => ({ type: UPLOAD_IMAGE_REQUEST });
export const uploadImageSuccess = () => ({ type: UPLOAD_IMAGE_SUCCESS });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  error: true,
  payload: error,
});
// ================ Thunks ================ //

export const uploadImage = image => async (dispatch, getState, sdk) => {
  dispatch(uploadImageRequest());

  // We need to create new FormData and
  // append the image to it, because the
  // SDK doesn't support uploading images
  // outside of listings
  const formData = new FormData();
  formData.append('image', image);

  const headers = {
    'content-type': 'multipart/form-data',
  };
  try {
    const response = await axios.post(uploadImageToGoogleStorageAPI, formData, {
      headers,
    });
    dispatch(uploadImageSuccess(response.data));
    return response.data;
  } catch (e) {
    dispatch(uploadImageError(storableError(e)));
  }
};

export const uploadDailyLogAttachments = images => (dispatch, getState, sdk) => {
  // Create an array of promises without delay
  const promises = images.map(image => dispatch(uploadImage(image)));

  // Wait for all promises to complete and handle errors
  return Promise.all(promises)
};
export const jobRunningCosts = (txId, jobCosts) => async (dispatch, getState, sdk) => {
  try {
    return updateMetaData({
      transactionId: txId,
      metadata: { jobCosts },
    })
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => console.log('e', e));
  } catch (e) {
    console.log('e', e)
  }
};

export const createDailyLog = (txId, payload, metadata) => async (dispatch, getState, sdk) => {

  dispatch(createDailyLogRequest());
  try {
    const { attachments, date, ...actionPayload } = payload;
    const promisedAttachments = attachments
      ? await dispatch(uploadDailyLogAttachments(attachments))
      : [];
    const mappedAttachments = promisedAttachments.filter(Boolean).map(a => a.link);
    const existingDailyLogs = metadata && metadata.dailyLogs ? metadata && metadata.dailyLogs : null;
    const dailyLogs = existingDailyLogs
      ? [
        {
          id: generateUUID(),
          date: new Date(date).toISOString().substring(0, 10),
          attachments: mappedAttachments,
          ...actionPayload,
        },
        ...existingDailyLogs,
      ]
      : [
        {
          id: generateUUID(),
          date: new Date(date).toISOString().substring(0, 10),
          attachments: mappedAttachments,
          ...actionPayload,
        },
      ];

    return updateMetaData({
      transactionId: txId,
      metadata: { dailyLogs },
    })
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        const { dailyLogs } = response.data.data.attributes.metadata || {};
        const { log_notes, mailSenderUserId, job_progress, issue, projectTitle, url } = dailyLogs[0];
        dispatch(sendEmailFordailyLogs({ log_notes, mailSenderUserId, job_progress, issue, projectTitle, url }));
        return response;
      })

      .catch(e => dispatch(createDailyLogError(storableError(e))));
  } catch (e) {
    dispatch(createDailyLogError(storableError(e)))
  }
};

export const createNewBills = (txId, bills) => async (dispatch, getState, sdk) => {

  try {
    return updateMetaData({
      transactionId: txId,
      metadata: { bills },
    })
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => console.log('e', e));
  } catch (e) {
    console.log('e', e)
  }
};

export const createCost = (txId, payload, previousCost, updatedCosts) => async (dispatch, getState, sdk) => {
  try {
    const { attachments, date, id, ...actionPayload } = payload;

    // Upload new attachments if provided
    const promisedAttachments = attachments
      ? await dispatch(uploadDailyLogAttachments(attachments))
      : [];

    const mappedAttachments = promisedAttachments.filter(Boolean).map(a => a.link);

    // Clone previous costs to avoid mutation
    let updatedCostList = Array.isArray(previousCost) ? [...previousCost] : [];

    if (id) {
      // Check if the ID exists in previousCost
      const index = updatedCostList.findIndex(cost => cost.id === id);
      if (index !== -1) {
        const existingAttachments = updatedCostList[index].attachments || [];

        updatedCostList[index] = {
          ...updatedCostList[index],
          date: date ? new Date(date).toISOString().substring(0, 10) : updatedCostList[index].date,
          attachments: mappedAttachments.length > 0
            ? [...existingAttachments, ...mappedAttachments]
            : existingAttachments,
          ...actionPayload,
        };
      } else {
        // If ID not found, add as new entry
        updatedCostList.push({
          id: generateUUID(),
          date: date ? new Date(date).toISOString().substring(0, 10) : '',
          attachments: mappedAttachments || [],
          ...actionPayload,
        });
      }
    } else {
      // Add new entry if ID is not provided
      updatedCostList.push({
        id: generateUUID(),
        date: date ? new Date(date).toISOString().substring(0, 10) : '',
        attachments: mappedAttachments || [],
        ...actionPayload,
      });
    }

    // Use updatedCosts if provided, otherwise use our modified cost list
    const createCost = updatedCosts ? updatedCosts : updatedCostList;

    return updateMetaData({
      transactionId: txId,
      metadata: { createCost },
    })
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => console.log('e', e));
  } catch (e) {
    console.log('e', e);
  }
};

const INBOX_PAGE_SIZE = 10;

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab, type } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const { page = 1 } = parse(search);
  const apiQueryParams = {
    //  only: onlyFilter,
    lastTransitions: type 
  ? LAST_TRANSITION[type] 
  :getAllTransitionsForEveryProcess().filter(t => t !== "transition/closed"),
    include: [
      'listing',
      'provider',
      'author',
      'provider.profileImage',
      'customer',
      'customer.profile.publicData',
      'provider.profile.publicData',
      'customer.profileImage',
      'customer.publicData',
      'booking',
      'listing.images'
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      "protectedData",
      "metadata",
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData','author'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
     'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};
